import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ArrowRight, ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";

export default function Quiz() {
  const [questions, setQuestions] = useState([]);
  const [step, setStep] = useState(0); // 0..questions.length-1 = questions, questions.length = capture
  const [answers, setAnswers] = useState({}); // { qid: value | [values] }
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/quiz");
        setQuestions(data.questions);
      } catch (e) {
        toast.error("Could not load the quiz. Please refresh.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="quiz-loading">
        <p className="overline">Preparing your quiz…</p>
      </div>
    );
  }

  const totalSteps = questions.length + 1; // + capture step
  const progress = Math.round(((step) / totalSteps) * 100);
  const onCapture = step === questions.length;
  const currentQ = !onCapture ? questions[step] : null;

  const selectSingle = (qid, value) => {
    setAnswers((a) => ({ ...a, [qid]: value }));
  };
  const toggleMulti = (qid, value, max) => {
    setAnswers((a) => {
      const curr = Array.isArray(a[qid]) ? a[qid] : [];
      if (curr.includes(value)) {
        return { ...a, [qid]: curr.filter((v) => v !== value) };
      }
      if (max && curr.length >= max) return a;
      return { ...a, [qid]: [...curr, value] };
    });
  };

  const canContinue = () => {
    if (onCapture) return form.name.trim().length > 0 && /\S+@\S+\.\S+/.test(form.email);
    const q = currentQ;
    const v = answers[q.id];
    if (q.type === "single") return Boolean(v);
    if (q.type === "multi") return Array.isArray(v) && v.length > 0;
    return false;
  };

  const next = () => setStep((s) => Math.min(s + 1, totalSteps - 1));
  const prev = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/submissions", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        answers,
      });
      navigate(`/result/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="quiz-page" className="min-h-screen flex flex-col">
      <Header />

      {/* Progress */}
      <div className="mx-auto w-full max-w-3xl px-6 sm:px-10 pt-12">
        <div className="flex items-center justify-between overline" data-testid="quiz-progress-label">
          <span>
            {onCapture ? "Your details" : `Question ${step + 1} of ${questions.length}`}
          </span>
          <span>{progress}%</span>
        </div>
        <div className="mt-4 h-[2px] w-full" style={{ background: "hsl(var(--taupe))" }}>
          <div
            data-testid="quiz-progress-bar"
            className="h-full transition-all duration-500"
            style={{ width: `${progress}%`, background: "hsl(var(--sage))" }}
          />
        </div>
      </div>

      <main className="flex-1 mx-auto w-full max-w-3xl px-6 sm:px-10 py-16 sm:py-24" data-testid="quiz-main">
        {!onCapture && currentQ && (
          <div key={currentQ.id} className="fade-up">
            <p className="overline mb-6" data-testid="quiz-section-label">{currentQ.section}</p>
            <h1 className="font-serif text-3xl sm:text-5xl leading-[1.08]" data-testid="quiz-question-text">
              {currentQ.question}
            </h1>
            {currentQ.type === "multi" && (
              <p className="mt-4 text-[hsl(var(--plum-soft))]" data-testid="quiz-multi-hint">
                Select up to {currentQ.max_select || 3}. {Array.isArray(answers[currentQ.id]) ? answers[currentQ.id].length : 0} selected.
              </p>
            )}

            <div className="mt-12 grid grid-cols-1 gap-4" data-testid="quiz-options">
              {currentQ.options.map((opt) => {
                const v = answers[currentQ.id];
                const selected =
                  currentQ.type === "single"
                    ? v === opt.value
                    : Array.isArray(v) && v.includes(opt.value);
                const onClick = () =>
                  currentQ.type === "single"
                    ? selectSingle(currentQ.id, opt.value)
                    : toggleMulti(currentQ.id, opt.value, currentQ.max_select);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={onClick}
                    data-testid={`quiz-option-${currentQ.id}-${opt.value}`}
                    className={`quiz-option text-left ${selected ? "selected" : ""}`}
                  >
                    <span
                      aria-hidden
                      className="mt-1 flex items-center justify-center shrink-0"
                      style={{
                        width: 22,
                        height: 22,
                        border: `1px solid ${selected ? "hsl(var(--gold))" : "hsl(var(--taupe))"}`,
                        borderRadius: currentQ.type === "multi" ? 2 : 9999,
                        background: selected ? "hsl(var(--gold))" : "transparent",
                      }}
                    >
                      {selected && <Check size={14} strokeWidth={2} color="hsl(var(--ivory))" />}
                    </span>
                    <span className="text-lg leading-snug">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {onCapture && (
          <div className="fade-up" data-testid="capture-step">
            <p className="overline mb-6">Almost there</p>
            <h1 className="font-serif text-3xl sm:text-5xl leading-[1.08]">
              Where should we send your result?
            </h1>
            <p className="mt-4 text-[hsl(var(--plum-soft))]">
              We'll show it on the next page. Your answers stay private.
            </p>

            <div className="mt-12 grid grid-cols-1 gap-10 max-w-xl">
              <div>
                <label className="overline block mb-2" htmlFor="name">Your name</label>
                <input
                  id="name"
                  data-testid="capture-name-input"
                  className="input-editorial"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Jane Practitioner"
                  autoFocus
                />
              </div>
              <div>
                <label className="overline block mb-2" htmlFor="email">Email</label>
                <input
                  id="email"
                  data-testid="capture-email-input"
                  type="email"
                  className="input-editorial"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@clinic.com"
                />
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="mt-16 flex items-center justify-between">
          <button
            type="button"
            onClick={prev}
            disabled={step === 0}
            data-testid="quiz-back-button"
            className="overline flex items-center gap-2 disabled:opacity-30 hover:text-[hsl(var(--gold))] transition-colors"
          >
            <ArrowLeft size={14} strokeWidth={1.5} /> Back
          </button>
          {!onCapture ? (
            <button
              type="button"
              onClick={next}
              disabled={!canContinue()}
              data-testid="quiz-next-button"
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Continue <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={!canContinue() || submitting}
              data-testid="quiz-submit-button"
              className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? "Scoring…" : "See my result"} <ArrowRight size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
